import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { first } from 'rxjs/operators';
import { combineLatest, firstValueFrom, forkJoin, from, map, Observable, switchMap, takeUntil } from 'rxjs';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { Equipment, OperationResourceAllocations, ProcessSheetRevision, ProcessSheets, Routing, Worker, RoutingOperation, TId, Tooling, Workcenter, Workforce, WorkProcedure, WorkProcedureStep, EDraftableStateType, OperationStateTypeColors, EOperationStateType, DraftableStateTypes } from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { RoutingAddComponent } from '../routing-add/routing-add.component';
import {GenericResource, IMixDrafteable, ProcessSheetsResource} from 'src/app/resources';
import { AlertService } from 'src/app/services/alert.service';
import { SelectionChangedEvent } from 'devextreme/ui/lookup';
import { RoutingOperationsAddComponent } from './components/routing-operations-add/routing-operations-add.component';
import { RoutingOperationsEditComponent } from './components/routing-operations-edit/routing-operations-edit.component';
import { ProcessSheetOperationService } from './process-sheet-operation.service';
import { WorkProcedureEditComponent } from './components/work-procedure-edit/work-procedure-edit.component';
import { TranslateService } from '@ngx-translate/core';
import {GenericCrudEditComponent} from "../../../../../../../libs/components/generic-crud/generic-crud-edit.component";
import {ProductsProcessSheetsEditComponent} from "../../products-process-sheets-edit.component";

@Component({
    selector: 'app-process-sheet-operations',
    templateUrl: './process-sheet-operations.component.html',
    styleUrl: './process-sheet-operations.component.sass',
    standalone: false
})
export class ProcessSheetOperationsComponent implements OnInit {
getOperationNames(ids: TId[]): string[] {
  if(ids && ids.length > 0) {
    return ids.map(id => this.routingOperations!.find(wf => wf.id === id)!.name);
  }
  return [];

}

  @Input() processSheet?: ProcessSheets;
  @Output() routingChangedEvent = new EventEmitter<TId>();

  routings?: GenericResource<Routing>;
  operaions = this.api.resources.product.routingOperations;
  procesSheetRevisions?: GenericResource<ProcessSheetRevision>;
  routingRevisions?: any;
  revisionsPopupVisible = false;
  loadingVisible = false;
  isDraft?:boolean;
  stateTypes: any
  isOperationDraft?:boolean;
  ready = false;
  routing?: Routing;
  routingId?: TId;
  draftableActive = EDraftableStateType.Active;
  routingOperations: (RoutingOperation & {
    workProcedure: WorkProcedure | null;
    steps: WorkProcedureStep[];
    resourceAllocations: any[];
  })[] = [];
  states: {[key: number]: string} = {};
  readonly stateColors: {[key: number]: string} = {
    [EDraftableStateType.Draft]: OperationStateTypeColors[EOperationStateType.Complete],
    [EDraftableStateType.Active]: OperationStateTypeColors[EOperationStateType.Start],
    [EDraftableStateType.Inactive]: OperationStateTypeColors[EOperationStateType.Complete],
    [EDraftableStateType.Obsolete]: OperationStateTypeColors[EOperationStateType.Complete],
  };

  editProcessSheetComponent = ProductsProcessSheetsEditComponent;
  processSheetRs = this.api.resources.product.processSheets;

  constructor(private api: MasterApiService, private tabsService: TabsService, private alerts: AlertService, private operationService: ProcessSheetOperationService, private tr: TranslateService) { }

  async ngOnInit(): Promise<void> {
    this.ready = false;
    DraftableStateTypes.forEach(s => this.states[s.id] = this.tr.instant('enumeration.DraftableStateTypes.' + s.name));
    this.stateTypes = DraftableStateTypes;
    this.procesSheetRevisions = this.api.resources.product.processSheetsRevisions(this.processSheet!.processSheetId, this.processSheet!.versionId);

    this.operationService.workcentersList = await firstValueFrom(this.api.resources.workcenters.getAll())
    this.routings = this.api.resources.product.routings.contextualizeQueryParams({'DocumentTypeId': this.processSheet!.documentTypeId.toString()});
    this.routingId = this.processSheet?.routingId;

    if (this.routingId) {
      this.routing = await firstValueFrom(this.routings.get(this.routingId!));
      this.getOperations();
    }
    if(this.processSheet?.stateId === EDraftableStateType.Draft) {
      this.isDraft = true;
    } else {
      this.isDraft = false;
    }
    this.ready = true;
  }

  async onRoutingChange($event: SelectionChangedEvent) {
    if (this.procesSheetRevisions) {
      const revision = await firstValueFrom(this.procesSheetRevisions.get(this.processSheet!.revisionId!));
      revision!.routingId = this.routingId!;
      await firstValueFrom(this.procesSheetRevisions.put(revision));
    }
    this.routing = await firstValueFrom(this.routings!.get(this.routingId!));
    await this.getOperations();

    this.routingChangedEvent.emit(this.routing?.id);
  }

  async getOperations(){
    this.loadingVisible = true;
    if (!this.routingId) return;

    const operations = await firstValueFrom(
      this.operaions(this.routingId).getAll()
    );

    const enrichedOps: (RoutingOperation & {
      workProcedure: WorkProcedure | null;
      steps: WorkProcedureStep[];
      resourceAllocations: any[];
    })[] = [];

    for (const op of operations) {

      let workProcedure = null;
      let steps: WorkProcedureStep[] = [];

      if(op.workProcedureId)
      {
        workProcedure = await firstValueFrom(
        this.api.resources.product.workProcedures.get(op.workProcedureId)

        );
        // steps (array), ordered by step.order for display
        const rawSteps = await firstValueFrom(
          this.api.resources.product.workProceduresSteps(op.workProcedureId).getAll()
        );
        steps = [...rawSteps].sort((a, b) => (Number(a.order ?? 0) - Number(b.order ?? 0)));
      }

      // resourceAllocations (array)
      const allocations = await firstValueFrom(
        this.api.resources.product
          .routingOperationsResourceAllocations(this.routingId, op.id)
          .getAll()
      );
      const mappedAllocations = allocations.map(allocation => {
        return {
          ...allocation,
          workCenters: this.groupBySection(allocation.workCenters),

          equipments: allocation.equipments,

          workforces: allocation.workforces,

          workers: allocation.workers,

          toolings: allocation.toolings
        };
      });

      enrichedOps.push({
        ...op,
        workProcedure,
        steps,
        resourceAllocations: mappedAllocations,
      });
    }

    this.routingOperations = enrichedOps;
    this.loadingVisible = false;
  }

  groupBySection(workCenters: any[]) {
    const map = new Map<string, any>();
    workCenters.forEach(wc => {
      if (!map.has(wc.sectionId)) {
        map.set(wc.sectionId, {
          sectionId: wc.sectionId,
          sectionName: wc.sectionName,
          workCenters: []
        });
      }
      map.get(wc.sectionId).workCenters.push(wc);
    });
    return Array.from(map.values());
  }

  async addRouting() {
    if(!this.isDraft) return;
    const tab = this.tabsService.createTab(RoutingAddComponent, TabRole.CREATION, {
      inputPrms: {
        processSheet: this.processSheet,
      }
    });
    tab.component.instance.entityCreated.pipe(
        takeUntil(combineLatest([tab.destroy$])),
      ).subscribe((entity) => {
        this.routingId = (entity as Routing).id;
        this.tabsService.closeTab(tab);
      });
  }

  async addOperation() {
    if(!this.isDraft || this.routing!.stateId === this.draftableActive) return;
    const tab = this.tabsService.createTab(RoutingOperationsAddComponent, TabRole.CREATION, {
      inputPrms: {
        routingId: this.routingId,
      }
    });
    tab.component.instance.entityCreated.pipe(
        takeUntil(combineLatest([tab.destroy$])),
      ).subscribe(async () => {
        await this.getOperations();
        this.tabsService.closeTab(tab);
      });
  }

  async openOperationEditor(operation: RoutingOperation) {
    if(!this.isDraft) return;
    const routing = await firstValueFrom(this.routings!.get(this.routingId!));
    const tab = this.tabsService.createTab(RoutingOperationsEditComponent, TabRole.EDITION, {
      inputPrms: {
        routing: routing,
        routingOperation: operation,
      }
    });
    tab.component.instance.entityChanged.pipe(
        takeUntil(combineLatest([tab.destroy$])),
      ).subscribe(async () => {
        this.tabsService.closeTab(tab);
      });
      tab.destroy$!.pipe(first()).subscribe(async () => {
        await this.getOperations();
      });
  }

  navigateToProcessSheetEditor(element: ProcessSheets) {
    // Close the popup first and defer the tab creation a tick, so the dx-popup
    // overlay (rendered on document.body) is torn down before this component is
    // switched to an inactive tab. Otherwise the overlay lingers and blocks the UI.
    this.revisionsPopupVisible = false;
    setTimeout(() => {
      this.tabsService.createTab<GenericCrudEditComponent<ProcessSheets>>(this.editProcessSheetComponent!, TabRole.EDITION, { inputPrms: {entity: element} });
    });
  }

  async openWorkProcedureEditor(workProcedure: WorkProcedure) {
    if(!this.isDraft) return;
    const tab = this.tabsService.createTab(WorkProcedureEditComponent, TabRole.EDITION, {
      inputPrms: {
        workProcedure: workProcedure,
        id: workProcedure.id
      }
    });
    tab.component.instance.entityChanged.pipe(
        takeUntil(combineLatest([tab.destroy$])),
      ).subscribe(async () => {
        await this.getOperations();
        this.tabsService.closeTab(tab);
      });
  }

  openRevisions(): void {
    if (!this.routingId) return;
    // Filter the revisions by the selected routing on the backend.
    this.routingRevisions = this.api.resources.product.processSheets.contextualizeQueryParams({
      RoutingId: this.routingId.toString(),
    });
    this.revisionsPopupVisible = true;
  }

  async copyRouting(){
    if(!this.isDraft) return;
    try {
      const copy = await firstValueFrom(this.api.routingClone(this.routingId!));
      this.routingId = copy.id;
      const revision = await firstValueFrom(this.procesSheetRevisions!.get(this.processSheet!.revisionId!));
      revision!.routingId = this.routingId!;
      await firstValueFrom(this.procesSheetRevisions!.put(revision!));
      this.alerts.info('Routing copied');
    } catch (error) {
      console.log(error);
    }
  }

  onReorder = async ($event: any) => {
    if(!this.isDraft) return;
    try {
      const movedItem = this.routingOperations.splice($event.fromIndex, 1)[0];
      this.routingOperations.splice($event.toIndex, 0, movedItem);
      this.routingOperations.forEach((op, idx) => {
        op.order = idx;
      });
      const operationId = movedItem.id;
      await firstValueFrom(
        this.api.reordeRoutingOperations(this.routingId!, operationId, { order: movedItem.order })
      );
    } catch (error) {
      console.error(error);
    }
  };

  async deleteOperation(operation: RoutingOperation) {
    if(!this.isDraft) return;
    if (!await this.alerts.confirm(this.tr.instant('common.deleteConfirmation', { num: 1 } ))) return;
    try {
      await firstValueFrom(this.operaions(this.routingId!).delete(operation.id));
      this.alerts.info(this.tr.instant('processSheet.operationDeleted'));
      await this.getOperations();
    } catch (error) {
      this.alerts.error(error as Error);
    }
  }

  toggleFullscreen() {
    const element = document.getElementById('fullScreen');

    if (this.isFullscreen()) {
      this.exitFullscreen();
    } else {
      this.openFullscreen(element);
    }
  }

  isFullscreen(): boolean {
    const documentWithFullscreen = document as Document & {webkitFullscreenElement?: Element, msFullscreenElement?: Element, mozFullScreenElement?: Element};

    return documentWithFullscreen.fullscreenElement != null ||
           documentWithFullscreen.webkitFullscreenElement != null ||
           documentWithFullscreen.mozFullScreenElement != null ||
           documentWithFullscreen.msFullscreenElement != null;
  }

  // Open the element in fullscreen
  openFullscreen(element: HTMLElement | null) {
    if (element) {
      // Type assertion to allow vendor-specific fullscreen methods
      element.classList.add('fullscreen-style');
      const elem = element as HTMLElement & {
        webkitRequestFullscreen?: () => void;
        mozRequestFullScreen?: () => void;
        msRequestFullscreen?: () => void;
      };

      // Check if the method is available and call the appropriate one
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) { // Safari and Chrome
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) { // Firefox
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) { // IE/Edge
        elem.msRequestFullscreen();
      }
    }
  }
  exitFullscreen() {
    document.getElementById('fullScreen')?.classList.remove('fullscreen-style');
    const documentWithFullscreen = document as Document & {webkitExitFullscreen?: () => void, mozCancelFullScreen?: () => void, msExitFullscreen?: () => void};

    if (documentWithFullscreen.exitFullscreen) {
      documentWithFullscreen.exitFullscreen();
    } else if (documentWithFullscreen.webkitExitFullscreen) {
      documentWithFullscreen.webkitExitFullscreen();
    } else if (documentWithFullscreen.mozCancelFullScreen) {
      documentWithFullscreen.mozCancelFullScreen();
    } else if (documentWithFullscreen.msExitFullscreen) {
      documentWithFullscreen.msExitFullscreen();
    }
  }




}
